import { Typography, Box, Button, useMediaQuery, useTheme, CircularProgress, Stack } from "@mui/material";
import useSWR, { useSWRConfig } from 'swr';
import QRCode from "qrcode.react";
import { PATH_NAME } from "../../../configs/pathName";
import { generalFetcher } from "../../../helpers/Fetchers/fetchers";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../../redux/features/redux-hooks";
import { logout } from "../../../redux/features/Auth/AuthThunk";
import { LinkCardPayload, IDirectPaymentPayload } from "../../../models/IWallet";
import { linkCard, paymentSettlement, startTransaction } from "../../../api/WalletAPI";
import { profileStyles } from '../../../styles/ProtectedPage/profile';
import RefreshIcon from "@mui/icons-material/Refresh";
import colorConfigs from "../../../configs/colorConfigs";
import { fonts, typo } from "../../../assets/fonts/font";
import Phone from '@mui/icons-material/LocalPhoneRounded';
import MessageBox from "../../common/MessageBox/MessageBox";
import assets from "../../../assets";
import { deleteAccountAPI } from "../../../api/AuthAPI";
import { getCurrentBrand } from "../../../configs/brandConfigs";

const Profile = () => {
  const activeBrand = getCurrentBrand();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { mutate } = useSWRConfig();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const size = isMobile ? 200 : 250;

  const hotlineNumber = '3129 8094';

  const { data: storeCreditsResponse } = useSWR(PATH_NAME.API_STORE_CREDITS_BALANCE, generalFetcher);
  const { data: walletResponse } = useSWR(PATH_NAME.API_STORE_CREDITS_WALLET, generalFetcher);
  const { data: basicUserInfo, error: userError } = useSWR(PATH_NAME.API_AUTH_PROFILE, generalFetcher);
  const { data: userProfile } = useSWR(PATH_NAME.API_AUTH_PROFILE, generalFetcher);
  const { data: userQrCode, error: qrError } = useSWR(PATH_NAME.API_QR_CODE, generalFetcher);
  const { data: paymentMethods } = useSWR(PATH_NAME.API_GET_MY_PAYMENT_METHODS, generalFetcher);

  const session_ref = localStorage.getItem("session_ref");
  const machine_id = localStorage.getItem("machine_id");

  const [isOverlayVisible, setIsOverlayVisible] = useState(false);
  const [qrValue, setQrValue] = useState(userQrCode || "");
  const lastRefreshTimeRef = useRef(Date.now());

  const [settlePaymentLoading, setSettlePaymentLoading] = useState(false);
  
  const storeCredits = storeCreditsResponse;

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const action = urlParams.get("action");

    if (action === "delete-account") {
      const confirmed = window.confirm("Are you sure you want to delete your account?");
      if (confirmed) {
        deleteAccountAPI().then(() => {
          dispatch(logout()).then(() => {
            navigate("/", { replace: true });
          });
        });
      } else {
        // Remove action from URL after cancel
        urlParams.delete("action");
        navigate({ search: urlParams.toString() }, { replace: true });
      }
    }
  }, [dispatch, navigate]);

  const handleViewMenuClick = () => {
    navigate(`${PATH_NAME.MENU}`);
  };

  useEffect(() => {
    const timer = setInterval(() => {
      const elapsedTime = Date.now() - lastRefreshTimeRef.current;
      if (elapsedTime >= 60000) {
        setIsOverlayVisible(true);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, []);
  
  useEffect(() => {
    if (userQrCode) {
      setQrValue(userQrCode);
    }
  }, [userQrCode]);

  const handleRefreshQrCode = () => {
    setIsOverlayVisible(false);
    window.location.reload();
    lastRefreshTimeRef.current = Date.now();
  };

  useEffect(() => {
    if (userError) {
      handleLogout();
    }
  });

  const handleLogout = async () => {
    try {
      await dispatch(logout()).unwrap();
      navigate(PATH_NAME.LOGIN);
    } catch (e) {
      console.error("Logout failed", e);
    }
  };

  if (qrError) return <div>QR Code Error</div>;
  if (!basicUserInfo) return <div>Loading...</div>;

  const handleLinkCard = async () => {
    try {
      const transactionResponse = await startTransaction();
      const transactionRef = `nommi-${transactionResponse.transaction_ref}`;

      const linkCardPayload: LinkCardPayload = {
        name: `${userProfile.firstname} ${userProfile.lastname}`,
        invoiceNo: transactionRef,
        description: "Link Card",
        amount: 0.01,
        payment_type: "credit_card", 
      };

      const response = await linkCard(linkCardPayload);

      const webPaymentUrl = response.result?.webPaymentUrl;

      if (webPaymentUrl) {
        window.location.href = webPaymentUrl;
      } else {
        console.error("Payment URL not found in the response");
      }
    } catch (error) {
      console.error("Error during Link Card process:", error);
    }
  };

  const handleCardClick = (cardId: number) => {
    navigate(`${PATH_NAME.CARD_DETAILS}${cardId}`);
  };
  
  const balanceNegative = storeCredits?.balance < 0;
  const noLinkedCard = !Array.isArray(paymentMethods) || paymentMethods.length === 0;

const handleSettlement = async () => {
  setSettlePaymentLoading(true);
  const transactionResponse = await startTransaction();
  if (transactionResponse) {
    const transactionRef = transactionResponse.transaction_ref;
    const paymentPayload: IDirectPaymentPayload = {
      name: transactionRef,
      invoiceNo: transactionRef,
      description: "settle store_credit",
      amount: parseFloat((Math.abs(storeCredits?.balance || 0)).toFixed(2)),
    };
    const response = await paymentSettlement(paymentPayload);
    if (response.success) {
      alert("Payment settled successfully.");
    } else {
      alert(`Failed to settle payment. ${response.error_message || "Failed to settle payment. Please try again."}`);
    }
  } else {
    alert("Failed to settle balance. Please try again.");
  }
  setSettlePaymentLoading(false);

  mutate(PATH_NAME.API_STORE_CREDITS_BALANCE);
  mutate(PATH_NAME.API_QR_CODE);
};

const getMessageBoxStatus = () => {
  if (storeCredits !== undefined) {
    if (storeCredits?.balance < 0 && !noLinkedCard) return "balanceNegativeWithCard";
    if (storeCredits?.balance < 0 && noLinkedCard) return "balanceNegativeWithoutCard";
  }
  return null;
}

const messageBoxStatus = getMessageBoxStatus();

const handleCardButton = () => {
  if(Array.isArray(paymentMethods) && paymentMethods.length > 0){
    const cardId = paymentMethods[0].id;
    handleCardClick(cardId)
  }else{
    handleLinkCard();
  }
}

const handleWalletClick = () => {
  navigate('/member/wallet');
};

  const walletDisplayItems = (() => {
    const items = Array.isArray(walletResponse?.items) ? walletResponse.items : [];

    const normalItem = items.find((item: any) => (item.card_type || '').toLowerCase() === 'normal');
    const otherItems = items.filter((item: any) => (item.card_type || '').toLowerCase() !== 'normal');

    const displayItems: Array<{ label: string; value: number }> = [];

    if (normalItem) {
      displayItems.push({
        label: 'Normal',
        value: Number(normalItem.balance || 0),
      });
    }

    if (otherItems.length > 0) {
      const othersTotal = otherItems.reduce((sum: number, item: any) => sum + Number(item.balance || 0), 0);
      displayItems.push({
        label: 'Others',
        value: othersTotal,
      });
    }

    return displayItems;
  })();

  return (
    <Box>
      <Box sx={profileStyles.container}>
        <Stack direction="column" spacing={1.5} alignItems="center">
          <Box sx={{...profileStyles.walletButton}} onClick={handleWalletClick}>
            <Box component="img" src={assets.images.wallet} alt="wallet" sx={{...profileStyles.buttonImage}} />
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "flex-start", paddingLeft:1}}>
              <Typography sx={{...typo.title}}>Wallet</Typography>
              <Typography sx={{...typo.sectionHeadline,lineHeight:1, color: `${storeCredits && storeCredits.balance < 5 ? colorConfigs.errorBg : colorConfigs.tertiary}`, fontFamily: fonts.bold}}>
                {storeCredits && storeCredits.balance !== undefined ? storeCredits.balance < 0 ? `$-${Math.abs(storeCredits.balance).toFixed(2)}` : `$${storeCredits.balance.toFixed(2)}` : "$0.00"}
                </Typography>
              {Number(storeCredits?.balance ?? 0) < 5 && (
                <Typography sx={{...typo.redText, fontSize: {xs: "0.75rem"}, fontColour: colorConfigs.tertiary, mt: 0, mb: 0}}>Min. $5 balance required</Typography>
              )}
              <Box sx={{ mt: 0, display: 'flex', flexDirection: 'column', gap: 0, width: '100%', alignItems: 'flex-start' }}>
                {walletDisplayItems.length > 0 ? (
                  walletDisplayItems.map((item, index) => (
                    <Typography key={`${item.label}-${index}`} sx={{...typo.smallText, fontSize: {xs: "0.72rem"}, lineHeight: 1}}>
                      {item.label}: ${Number(item.value || 0).toFixed(2)}
                    </Typography>
                  ))
                ) : (
                  <Typography sx={{...typo.smallText, fontSize: {xs: "0.72rem"}, lineHeight: 1.3}}>
                    No wallet items yet
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
          <Stack direction="row" spacing={1.5} sx={{ width: {xs:"100%",sm:"80%"}, justifyContent: "center" }}>
          <Box sx={{...profileStyles.linkCardButton2}} onClick={handleCardButton}>
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center"}}>
              <Stack direction="row" alignItems="center">
                {noLinkedCard && (
                  <>
                    <Box component="img" src={assets.images.creditCard} alt="credit card" sx={{...profileStyles.buttonImage}} />
                    <Typography sx={{...typo.title, lineHeight: 1.25,fontSize:{xs:'16px',sm:'18px'}, textAlign:'left'}}>Link Credit Card</Typography>
                  </>
                )}
                {!noLinkedCard && (
                  <>
                    <Box component="img" src={assets.images.greenTick} alt="Tick" sx={{...profileStyles.buttonImage}} />
                    <Stack direction="column" alignItems="start">
                      <Typography sx={{...typo.title, lineHeight: 1, textAlign: "left", fontSize:{xs:'16px',sm:'18px'}}}>Credit Card</Typography>
                      <Typography sx={{...typo.smallText,fontSize:{xs:'16px',sm:'16px'} ,lineHeight: 1.5,fontFamily: fonts.bold}}>
                        {`*${paymentMethods && paymentMethods.length > 0 && paymentMethods[0].card_last_four}`}
                      </Typography>
                    </Stack>
                  </>
                )}
            </Stack>
              <Typography sx={{...typo.smallText, fontSize: {xs: "0.75rem"}}}>For fuss-free payments</Typography>
            </Box>
          </Box>
          <Box sx={{...profileStyles.topupButton}} onClick={() => navigate(PATH_NAME.TOPUP)}>
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center"}}>
              <Typography sx={{...typo.title, lineHeight: 1.25, color: colorConfigs.secondary, fontSize:{xs:'16px',sm:'18px'}, textAlign:'left'}}>Top up wallet</Typography>
            </Box>
          </Box>
          </Stack>
          {session_ref && machine_id && (
            <Button
              variant="contained"
              sx={{...typo.button, ...profileStyles.viewmenubutton}}
              onClick={handleViewMenuClick}
            >
              <Box
                component="img"
                src={assets.images.ViewMenu}
                alt="viewmenu"
                sx={{ ...profileStyles.buttonImage }}
              />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Typography
                  sx={{
                    ...typo.title,
                    lineHeight: 1.25,
                    color: colorConfigs.tertiary,
                    fontSize: { xs: "16px", sm: "18px" },
                    textAlign: "center",
                    textTransform:'none',
                  }}
                >
                  View Menu
                </Typography>
              </Box>
            </Button>
          )}
        </Stack>
        <Typography variant="caption" display="block" sx={{...profileStyles.qrCodeCaption, ...typo.smallText, lineHeight: 1.25, fontFamily: fonts.bold}}>
          Place this QR code against the <br />scanner to unlock the fridge
        </Typography>
        <Box sx={profileStyles.qrCodeBox}>
          <QRCode
            value={qrValue}
            size={size}
            bgColor={activeBrand?.colors.white}
            fgColor={activeBrand?.colors.tertiary}
            level="H"
            includeMargin={true}
          />

          {isOverlayVisible && (
            <Box sx={profileStyles.qrOverlayBox(size, isMobile)}>
              <Button onClick={handleRefreshQrCode} variant="contained" sx={profileStyles.refreshButton(size)}>
                <RefreshIcon sx={profileStyles.refreshIcon(isMobile)} />
                <Typography variant="button" sx={{...typo.button, color: colorConfigs.secondary}}>CLICK HERE TO REFRESH QR CODE</Typography>
              </Button>
            </Box>
          )}
          {messageBoxStatus === "balanceNegativeWithCard" && (
            <Box sx={profileStyles.qrOverlayBox(size, isMobile)}>
              <MessageBox message="The credit card deduction for your recent transaction was not successful. Please pay the outstanding balance to use the fridge." />
            </Box>
          )}
           {messageBoxStatus === "balanceNegativeWithoutCard" && (
              <Box sx={profileStyles.qrOverlayBox(size, isMobile)}>
                <MessageBox message="Please top-up wallet balance (min. $5 required) or link credit card to use smart fridge." />
              </Box>
            )}
        </Box>

        {balanceNegative && !noLinkedCard && (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mt: 2 }}>
            <Button
              variant="contained"
              sx={{ ...profileStyles.settleBalanceButton }}
              onClick={handleSettlement}
              disabled={settlePaymentLoading} // disable while loading
            >
              {settlePaymentLoading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1, color: colorConfigs.white }} />
                  Processing... 
                </>
              ) : (
                "Pay with Linked Card"
              )}
            </Button>
          </Box>
        )}
        </Box>
      <Box sx={{marginTop:5, textAlign:"center"}}>
        <Typography sx={{...typo.smallText, fontFamily: fonts.bold}}>Need help? Chat with us!</Typography>
        <Typography sx={{...typo.smallText , fontSize:'12px'}}>We’re available on weekdays from 9am-9pm,</Typography>
        <Typography sx={{...typo.smallText , fontSize:'12px'}}>excluding public holidays</Typography>
      </Box>

      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent:'center',
        margin:'20px auto',
        width: {xs:'70%', sm:'23%'},
        padding: '15px 30px',
        backgroundColor: colorConfigs.tertiary,
        borderRadius: '10px',
        
      }}>
        <Phone sx={{ background:colorConfigs.secondary, color:colorConfigs.tertiary, borderRadius:"50%", width:'50px', height:'auto', padding:'10px', fontSize:'40px' }} />
        <Stack direction="column" sx={{display: "flex",  alignItems: 'center', justifyContent:'center', gap: 0}}>
          <Typography
            sx={{
              ...typo.title,
              fontSize:'22px',
              marginLeft:1,
              color: colorConfigs.secondary,
              lineHeight: 1.25,
              textAlign: "center",
            }}
          >
            {hotlineNumber} 
          </Typography>
          <Typography
            sx={{
              ...typo.smallText,
              fontSize:'14px',
              color: colorConfigs.secondary,
              lineHeight: 1,
              textAlign: "center",
              fontFamily: fonts.bold
            }}
          >
            (TOLL-FREE)
          </Typography>
        </Stack>
      </Box>
    </Box>
    
  );
};

export default Profile;